"use client";

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Bell, Check, Loader2, Pause, X } from "lucide-react";
import { navigate } from "wouter/use-browser-location";
import {
  Virtualizer,
  type CustomContainerComponentProps,
  type CustomItemComponentProps,
} from "virtua";
import { useNotificationContext } from "@/components/notification-provider";
import { buildNotificationSessionPath, isSafeNotificationActionUrl } from "@/lib/notification-deep-link";
import { formatTaskDueDate, isCalendarDate } from "@/lib/task-due-date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { ScheduleRunSummary, ScheduleRunsResponse } from "@/api/views";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  InboxNotification,
  InboxNotificationView,
} from "@/hooks/use-inbox-notifications";

function relativeTime(timestamp: string, locale: string): string {
  const date = new Date(timestamp);
  const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const ranges: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, seconds] of ranges) {
    if (Math.abs(deltaSeconds) >= seconds) {
      return formatter.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return formatter.format(deltaSeconds, "second");
}

function statusFromEvent(notification: InboxNotification): string | undefined {
  const [, status] = notification.eventType.split(".", 2);
  return status || undefined;
}

function requiresAction(notification: InboxNotification): boolean {
  return statusFromEvent(notification) === "paused" || notification.priority > 0;
}

interface InboxVirtualListContextValue {
  notifications: InboxNotification[];
  view: InboxNotificationView;
}

const InboxVirtualListContext = createContext<InboxVirtualListContextValue | null>(null);

const InboxVirtualList = forwardRef<HTMLUListElement, CustomContainerComponentProps>(
  ({ style, children }, ref) => {
    const { t } = useTranslation();
    const context = useContext(InboxVirtualListContext);
    return (
      <ul
        ref={ref}
        style={style}
        className="divide-y"
        aria-label={context?.view === "all"
          ? t("notifications.inbox.allNotifications")
          : t("notifications.inbox.unreadNotifications")}
      >
        {children}
      </ul>
    );
  },
);
InboxVirtualList.displayName = "InboxVirtualList";

const InboxVirtualRow = forwardRef<HTMLLIElement, CustomItemComponentProps>(
  ({ style, index, children }, ref) => {
    const context = useContext(InboxVirtualListContext);
    const notification = context?.notifications[index];
    const isRead = notification?.readAt != null;
    const className = notification
      ? isRead
        ? "relative opacity-60"
        : requiresAction(notification)
          ? "relative bg-amber-50/70 dark:bg-amber-950/20"
          : "relative"
      : "relative";

    return (
      <li
        ref={ref}
        style={style}
        data-read={isRead || undefined}
        className={`${index > 0 ? "border-t " : ""}${className}`}
      >
        {children}
      </li>
    );
  },
);
InboxVirtualRow.displayName = "InboxVirtualRow";

function NotificationInboxRow({
  notification,
  onOpen,
  onHistory,
  onDismiss,
}: {
  notification: InboxNotification;
  onOpen: (notification: InboxNotification) => void;
  onHistory: (event: MouseEvent<HTMLButtonElement>, notification: InboxNotification) => void;
  onDismiss: (event: MouseEvent<HTMLButtonElement>, notificationId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRead = notification.readAt !== null;
  const reminder = notification.eventType === "task.due_reminder"
    && notification.reminderTranslation?.titleKey === "projects:dueReminderTitle"
    && notification.reminderTranslation?.bodyKey === "projects:dueReminderBody"
    && isCalendarDate(notification.reminderTranslation.values.date)
    ? notification.reminderTranslation.values : null;
  const reminderText = reminder
    ? { task: reminder.task, date: formatTaskDueDate(reminder.date, i18n.language) }
    : null;

  return (
    <>
      <button
        type="button"
        className="flex w-full items-start gap-3 px-4 py-3 pr-12 text-left transition-colors hover:bg-accent"
        onClick={() => onOpen(notification)}
      >
        {isRead ? (
          <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        ) : requiresAction(notification) ? (
          <Pause className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        ) : (
          <Bell className="mt-0.5 size-4 shrink-0 text-primary" />
        )}
        <span className="min-w-0 flex-1">
          {requiresAction(notification) && (
            <span className="mb-0.5 block text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
              {t("notifications.inbox.actionRequired")}
            </span>
          )}
          <span className={`block truncate text-sm font-medium ${isRead ? "text-muted-foreground" : ""}`}>
            {reminderText ? t("projects:dueReminderTitle", reminderText) : notification.title ?? t("notifications.inbox.agentUpdate")}
          </span>
          {notification.body && (
            <span className="mt-0.5 block text-sm text-muted-foreground">
              {reminderText ? t("projects:dueReminderBody", reminderText) : notification.body}
            </span>
          )}
          <span className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            {isRead && (
              <span className="inline-flex items-center gap-1 font-medium">
                <Check className="size-3" />
                {t("notifications.inbox.read")}
              </span>
            )}
            <span>{relativeTime(notification.createdAt, i18n.language)}</span>
          </span>
        </span>
      </button>
      {notification.eventType === "schedule.completed" && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="ml-10 mb-2 h-auto p-0 text-xs"
          onClick={(event) => onHistory(event, notification)}
        >
          {t("scheduleRuns.runHistory")}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="absolute right-2 top-1/2 -translate-y-1/2"
        aria-label={t("notifications.inbox.dismiss")}
        onClick={(event) => onDismiss(event, notification.id)}
      >
        <X className="size-3.5" />
      </Button>
    </>
  );
}

/** Header button plus a durable inbox dropdown with unread and all views. */
export function NotificationInbox() {
  const { t } = useTranslation();
  const { inbox } = useNotificationContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [history, setHistory] = useState<{
    open: boolean; loading: boolean; error: string | null; runs: ScheduleRunSummary[];
    total: number; available: boolean | null;
  }>({ open: false, loading: false, error: null, runs: [], total: 0, available: null });

  const openNotification = useCallback(async (notification: InboxNotification) => {
    await inbox.markRead(notification.id);
    if (isSafeNotificationActionUrl(notification.actionUrl)) {
      navigate(notification.actionUrl);
      return;
    }
    navigate(buildNotificationSessionPath({
      session_id: notification.sessionId,
      run_id: notification.runId,
      status: statusFromEvent(notification),
    }));
  }, [inbox]);

  const dismissNotification = useCallback(async (
    event: MouseEvent<HTMLButtonElement>,
    notificationId: string,
  ) => {
    event.stopPropagation();
    await inbox.dismiss(notificationId);
  }, [inbox]);

  const openHistory = useCallback(async (
    event: MouseEvent<HTMLButtonElement>, notification: InboxNotification,
  ) => {
    event.stopPropagation();
    setMenuOpen(false);
    setHistory({ open: true, loading: true, error: null, runs: [], total: 0, available: null });
    if (!notification.scheduleHistoryUrl || !inbox.fetchScheduleHistory) {
      setHistory((previous) => ({ ...previous, loading: false, error: t("scheduleRuns.serverUpgradeRequired") }));
      return;
    }
    try {
      const response = await inbox.fetchScheduleHistory(notification.scheduleHistoryUrl);
      if (!response.ok) {
        throw new Error(response.status === 404 || response.status === 422
          ? t("scheduleRuns.serverUpgradeRequired") : t("scheduleRuns.unavailable"));
      }
      const body = await response.json() as Partial<ScheduleRunsResponse>;
      if (!Array.isArray(body.runs) || !Number.isSafeInteger(body.total) || body.total! < 0) {
        throw new Error(t("scheduleRuns.invalidResponse"));
      }
      setHistory((previous) => ({ ...previous, loading: false, runs: body.runs!, total: body.total!,
        available: typeof body.history_available === "boolean" ? body.history_available : null }));
    } catch (error) {
      setHistory((previous) => ({ ...previous, loading: false,
        error: error instanceof Error ? error.message : t("scheduleRuns.unavailable") }));
    }
  }, [inbox, t]);

  const loadMoreIfNearEnd = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !inbox.hasMore || inbox.loadingMore) return;
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remaining <= 160) {
      void inbox.loadMore().catch(() => {
        // The hook exposes a stale state and schedules the next poll on error.
      });
    }
  }, [inbox]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    // If the first page is shorter than the dropdown viewport, make more
    // history reachable without requiring a scroll event that cannot happen.
    if (container && container.clientHeight > 0) loadMoreIfNearEnd();
  }, [inbox.notifications.length, loadMoreIfNearEnd]);

  return (
    <>
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative size-7 shrink-0"
          aria-label={inbox.unreadCount > 0
            ? t("notifications.inbox.unreadCount", { count: inbox.unreadCount })
            : t("notifications.inbox.title")}
          data-testid="notification-inbox-trigger"
        >
          <Bell className="size-4" />
          {inbox.unreadCount > 0 && (
            <Badge
              asChild
              variant="destructive"
              className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full px-1 py-0 text-[10px] shadow-none"
            >
              <span aria-hidden="true">
                {inbox.unreadCount > 99 ? "99+" : inbox.unreadCount}
              </span>
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="flex max-h-[400px] w-80 flex-col gap-0 overflow-hidden p-0"
      >
        <DropdownMenuLabel className="border-b px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold">{t("notifications.inbox.title")}</span>
            <ButtonGroup
              aria-label={t("notifications.inbox.view")}
              data-testid="notification-view-toggle"
              className="shrink-0 rounded-md border border-border bg-muted/60 p-0.5 shadow-none"
            >
              <Button
                type="button"
                aria-pressed={inbox.view === "unread"}
                size="sm"
                variant="ghost"
                className={`h-6 px-2 text-xs ${inbox.view === "unread"
                  ? "bg-background text-foreground shadow-sm hover:bg-background"
                  : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => inbox.setView("unread")}
              >
                {t("notifications.inbox.unread")}
              </Button>
              <Button
                type="button"
                aria-pressed={inbox.view === "all"}
                size="sm"
                variant="ghost"
                className={`h-6 px-2 text-xs ${inbox.view === "all"
                  ? "bg-background text-foreground shadow-sm hover:bg-background"
                  : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => inbox.setView("all")}
              >
                {t("notifications.inbox.all")}
              </Button>
            </ButtonGroup>
          </div>
        </DropdownMenuLabel>
        {inbox.status === "stale" && inbox.notifications.length > 0 && (
          <div className="border-b bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            {t("notifications.inbox.reconnecting")}
          </div>
        )}
        <div
          ref={scrollContainerRef}
          className="min-h-0 flex-1 overflow-y-auto"
          onScroll={loadMoreIfNearEnd}
        >
          {inbox.loading && inbox.notifications.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t("notifications.inbox.loading")}
            </div>
          ) : inbox.status === "error" && inbox.notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-amber-700 dark:text-amber-300">
              <X className="size-5" />
              <span>{t("notifications.inbox.refreshFailed")}</span>
              <span className="text-xs text-muted-foreground">
                {inbox.error ?? t("notifications.inbox.retryAutomatically")}
              </span>
            </div>
          ) : inbox.notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground">
              {inbox.status === "stale" ? (
                <>
                  <Loader2 className="size-5 animate-spin text-amber-600 dark:text-amber-400" />
                  <span>{t("notifications.inbox.stale")}</span>
                  <span className="text-xs">{t("notifications.inbox.retryAutomatically")}</span>
                </>
              ) : (
                <>
                  <Check className="size-5" />
                  {inbox.view === "all"
                    ? t("notifications.inbox.noneYet")
                    : t("notifications.inbox.caughtUp")}
                </>
              )}
            </div>
          ) : (
            <>
              <InboxVirtualListContext.Provider value={{
                notifications: inbox.notifications,
                view: inbox.view,
              }}>
                <Virtualizer<InboxNotification>
                  data={inbox.notifications}
                  as={InboxVirtualList}
                  item={InboxVirtualRow}
                  scrollRef={scrollContainerRef}
                  itemSize={88}
                  bufferSize={440}
                  ssrCount={Math.min(inbox.notifications.length, 10)}
                  // Indices are only ever valid while they exist in `data`.
                  // openNotification() removes the clicked row optimistically,
                  // so a previously keepMounted index can exceed the shrunk
                  // array length on the very next render — virtua then re-
                  // invokes the children renderer with `undefined` for it
                  // (mobile crash when selecting an in-app notification).
                  // Bound the set to the current data length so removed rows
                  // are simply un-mounted instead of becoming a dead slot.
                  keepMounted={inbox.notifications.length <= 10
                    ? inbox.notifications.map((_notification, index) => index)
                    : undefined}
                >
                  {(notification, index) =>
                    // virtua can invoke this children renderer with `undefined`
                    // for a `keepMounted` index after the clicked row is
                    // optimistically removed from `data` by markRead (mobile
                    // crash when selecting an in-app notification). We must
                    // return an element (virtua reads its `.key`), so mount
                    // an empty spacer instead of rendering a dead row.
                    notification ? (
                      <NotificationInboxRow
                        key={notification.id}
                        notification={notification}
                        onOpen={(selected) => void openNotification(selected)}
                        onHistory={(event, selected) => void openHistory(event, selected)}
                        onDismiss={(event, notificationId) => void dismissNotification(event, notificationId)}
                      />
                    ) : (
                      <span key={`removed-${index}`} aria-hidden="true" />
                    )
                  }
                </Virtualizer>
              </InboxVirtualListContext.Provider>
              {inbox.loadingMore ? (
                <div className="flex items-center justify-center gap-2 border-t px-4 py-3 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" />
                  {t("notifications.inbox.loadingMore")}
                </div>
              ) : inbox.hasMore ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full rounded-none border-t text-xs text-muted-foreground"
                  onClick={() => void inbox.loadMore().catch(() => {})}
                >
                  {t("notifications.inbox.loadMore")}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    <Dialog open={history.open} onOpenChange={(open) => setHistory((previous) => ({ ...previous, open }))}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("scheduleRuns.title")}</DialogTitle></DialogHeader>
        {history.loading ? <p role="status">{t("scheduleRuns.loading")}</p>
          : history.error ? <p role="alert">{history.error}</p>
          : history.available === false ? <p role="status">{t("scheduleRuns.completedHistoryUnavailable")}</p>
          : <>
            {history.available === null && <p role="status">{t("scheduleRuns.historyUnknownOlderServer")}</p>}
            <p className="text-sm text-muted-foreground">{t("scheduleRuns.count", { shown: history.runs.length, total: history.total })}</p>
            <ul className="space-y-3">
              {history.runs.map((run, index) => (
                <li key={run.id ?? `${run.run_id ?? "run"}-${index}`} className="rounded border p-3 text-sm">
                  <strong>{run.status}</strong>
                  {run.triggered_at != null && <span className="ml-2 text-muted-foreground">{new Date(run.triggered_at * 1000).toLocaleString()}</span>}
                  {(run.error || run.output_excerpt) && <p className="mt-1 whitespace-pre-wrap">{run.error || run.output_excerpt}</p>}
                </li>
              ))}
            </ul>
          </>}
      </DialogContent>
    </Dialog>
    </>
  );
}
